import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import { practicePaymentApi } from "apis";
import { CreateCheckoutSessionRequest } from "libs/cellera-sdk";
import React, { useState, useCallback } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import {
  PAYMENT_CONSTANTS,
  PLAN_PERIODS,
  formatCurrency,
  PLAN_PERIOD_DESCRIPTIONS,
  PlanInterval,
  PlanDisplayName,
  PaymentUILabels,
  PaymentErrorMessages,
} from "../../constants/payment";

const ONBOARDING_FEE = PAYMENT_CONSTANTS.ONBOARDING_FEE;

const plans = [
  {
    id: PlanInterval.MONTHLY,
    title: PlanDisplayName.MONTHLY,
    price: PAYMENT_CONSTANTS.MONTHLY_PLAN_PRICE,
    period: PLAN_PERIODS.MONTHLY,
    description: `${formatCurrency(PAYMENT_CONSTANTS.MONTHLY_PLAN_PRICE)} ${PLAN_PERIOD_DESCRIPTIONS.MONTHLY}`,
  },
  {
    id: PlanInterval.SIX_MONTHS,
    title: PlanDisplayName.SIX_MONTHS,
    price: PAYMENT_CONSTANTS.SIX_MONTH_PLAN_PRICE,
    period: PLAN_PERIODS.SIX_MONTHS,
    description: `${formatCurrency(PAYMENT_CONSTANTS.SIX_MONTH_PLAN_PRICE)} ${PLAN_PERIOD_DESCRIPTIONS.SIX_MONTHS}`,
  },
];

const PaymentSelection: React.FC = () => {
  const [loadingPlan, setLoadingPlan] = useState<PlanInterval | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanInterval | null>(null);
  const { colors } = useCelleraTheme();

  const handleCheckout = useCallback(
    async (planId: PlanInterval) => {
      try {
        setLoadingPlan(planId);
        setError(null);

        const dto: CreateCheckoutSessionRequest = {
          createCheckoutSessionDto: {
            subscriptionPlan: planId,
          },
        };

        // Use the raw API method to access response data
        const apiResponse =
          await practicePaymentApi.createCheckoutSessionRaw(dto);

        if (apiResponse.raw.ok) {
          const responseData = await apiResponse.raw.json();

          if (responseData?.url) {
            window.location.href = responseData.url;
          } else {
            setError("Checkout URL not found in response. Please try again.");
          }
        } else {
          throw new Error(
            `API request failed with status: ${apiResponse.raw.status}`
          );
        }
      } catch (error) {
        console.error("Failed to create checkout session:", error);
        setError(PaymentErrorMessages.GENERIC_ERROR);
      } finally {
        setLoadingPlan(null);
      }
    },
    [setLoadingPlan, setError]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.background.default,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 4,
      }}
    >
      {/* Main Content Container */}
      <Box sx={{ maxWidth: 600, width: "100%" }}>
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            gutterBottom
            sx={{
              color: colors.text.primary,
              mb: 3,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Join Cellera Today
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              mb: 2,
              lineHeight: 1.6,
              fontSize: "1.1rem",
            }}
          >
            Cellera empowers you with intelligent, accessible healthcare -
            anytime, anywhere.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              mb: 4,
              lineHeight: 1.6,
              fontSize: "1.1rem",
            }}
          >
            To begin using Cellera, a one-time onboarding fee and a subscription
            plan are required. This covers setup and access to our medical
            network.
          </Typography>
        </Box>

        {/* Pricing Sections */}
        <Box sx={{ mb: 4 }}>
          {/* Onboarding Fee Section */}
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: colors.border.light,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                mt: 1,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: "colors.text.secondary",
                  fontSize: "0.875rem",
                }}
              >
                1
              </Typography>
            </Box>
            <Paper
              sx={{
                flex: 1,
                p: 3,
                backgroundColor: colors.background.paper,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                border: `1px solid ${colors.border.light}`,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  sx={{ color: "colors.text.primary" }}
                >
                  Onboarding fee
                </Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, color: "white" }}
                      >
                        The initial $25,000 Onboarding Fee secures the
                        foundation and activation of your clinic&apos;s portal
                        services and current modalities. It is expressly
                        understood that this fee excludes all future service
                        expansions.
                      </Typography>
                      <Typography variant="body2" sx={{ color: "white" }}>
                        Any requests for system upgrades, new feature
                        implementations, or the incorporation of additional
                        clinical modalities will require a separate evaluation
                        and corresponding fee structure based on the labor and
                        resources involved
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 400,
                        bgcolor: "rgba(0, 0, 0, 0.87)",
                        fontSize: "0.875rem",
                        p: 2,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: colors.text.secondary,
                      "&:hover": {
                        color: colors.primary.main,
                      },
                    }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ color: "colors.text.primary" }}
              >
                {formatCurrency(ONBOARDING_FEE)}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                {PLAN_PERIODS.PERMANENT}
              </Typography>
            </Paper>
          </Box>

          {/* Subscription Plans Section */}
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: colors.border.light,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                mt: 1,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: "colors.text.secondary",
                  fontSize: "0.875rem",
                }}
              >
                2
              </Typography>
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", gap: 2, alignItems: "center" }}
            >
              {/* 6-month Plan */}
              <Paper
                onClick={() => setSelectedPlan(PlanInterval.SIX_MONTHS)}
                sx={{
                  flex: 1,
                  p: 3,
                  backgroundColor:
                    selectedPlan === PlanInterval.SIX_MONTHS
                      ? colors.primary.light
                      : colors.background.paper,
                  borderRadius: 2,
                  boxShadow:
                    selectedPlan === PlanInterval.SIX_MONTHS
                      ? "0 4px 12px rgba(26, 54, 93, 0.2)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                  border:
                    selectedPlan === PlanInterval.SIX_MONTHS
                      ? `2px solid ${colors.primary.main}`
                      : `1px solid ${colors.border.light}`,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  sx={{ color: "colors.text.primary", mb: 1 }}
                >
                  {PlanDisplayName.SIX_MONTHS}
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{ color: "colors.text.primary" }}
                >
                  {formatCurrency(PAYMENT_CONSTANTS.SIX_MONTH_PLAN_PRICE)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.disabled }}
                >
                  /6 months
                </Typography>
                {selectedPlan === PlanInterval.SIX_MONTHS && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: colors.primary.main,
                      color: "colors.background.paper",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    ✓ Selected
                  </Box>
                )}
              </Paper>

              {/* OR separator */}
              <Typography
                variant="body1"
                sx={{
                  color: "colors.text.tertiary",
                  fontWeight: "medium",
                }}
              >
                or
              </Typography>

              {/* Monthly Plan */}
              <Paper
                onClick={() => setSelectedPlan(PlanInterval.MONTHLY)}
                sx={{
                  flex: 1,
                  p: 3,
                  backgroundColor:
                    selectedPlan === PlanInterval.MONTHLY
                      ? colors.primary.light
                      : colors.background.paper,
                  borderRadius: 2,
                  boxShadow:
                    selectedPlan === PlanInterval.MONTHLY
                      ? "0 4px 12px rgba(26, 54, 93, 0.2)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                  border:
                    selectedPlan === PlanInterval.MONTHLY
                      ? `2px solid ${colors.primary.main}`
                      : "1px solid colors.border.light",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  sx={{ color: "colors.text.primary", mb: 1 }}
                >
                  {PlanDisplayName.MONTHLY}
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{ color: "colors.text.primary" }}
                >
                  {formatCurrency(PAYMENT_CONSTANTS.MONTHLY_PLAN_PRICE)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.disabled }}
                >
                  /month
                </Typography>
                {selectedPlan === PlanInterval.MONTHLY && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: colors.primary.main,
                      color: "colors.background.paper",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    ✓ Selected
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: colors.error[50],
              border: `1px solid ${colors.error[200]}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" sx={{ color: colors.error[700] }}>
              {error}
            </Typography>
          </Paper>
        )}

        {/* Selected Plan Summary */}
        {selectedPlan && (
          <Box
            sx={{
              mb: 3,
              p: 3,
              backgroundColor: colors.background.secondary,
              borderRadius: 2,
              border: `1px solid ${colors.border.light}`,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: colors.text.primary, mb: 1 }}
            >
              {PaymentUILabels.SELECTED_PLAN_SUMMARY}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              Onboarding Fee: $
              {ONBOARDING_FEE.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}{" "}
              + {plans.find((p) => p.id === selectedPlan)?.description}
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: colors.primary.main, mt: 1 }}
            >
              Total: $
              {(
                ONBOARDING_FEE +
                (plans.find((p) => p.id === selectedPlan)?.price || 0)
              ).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        )}

        {/* Main CTA Button */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Button
            variant="contained"
            disabled={!selectedPlan || loadingPlan !== null}
            onClick={() => {
              if (selectedPlan) {
                handleCheckout(selectedPlan);
              }
            }}
            startIcon={
              loadingPlan ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              backgroundColor: colors.primary.main,
              color: colors.primary.contrast,
              py: 2,
              px: 4,
              fontSize: "1.1rem",
              fontWeight: "bold",
              borderRadius: 2,
              minWidth: 280,
              "&:hover": {
                backgroundColor: "colors.primary.main",
              },
              "&:disabled": {
                backgroundColor: colors.primary.main,
                color: colors.primary.contrast,
              },
            }}
          >
            {loadingPlan
              ? PaymentUILabels.PROCESSING
              : !selectedPlan
                ? PaymentUILabels.SELECT_PLAN_FIRST
                : PaymentUILabels.PROCEED_TO_PAYMENT}
          </Button>
        </Box>

        {/* Compliance Badges */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          {/* HIPAA Compliant Badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: colors.primary.main,
              color: colors.primary.contrast,
              px: 2,
              py: 1,
              borderRadius: 1,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: colors.background.paper,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.primary.main,
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              >
                ⚕
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.primary.contrast,
                fontWeight: "bold",
                fontSize: "0.8rem",
              }}
            >
              HIPAA COMPLIANT
            </Typography>
          </Box>

          {/* Powered by Stripe Badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: colors.background.paper,
              color: colors.text.primary,
              px: 2,
              py: 1,
              borderRadius: 1,
              border: `1px solid ${colors.border.light}`,
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                fontSize: "0.8rem",
              }}
            >
              Powered by
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontWeight: "bold",
                fontSize: "0.8rem",
              }}
            >
              stripe
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSelection;
